import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';

export default function About() {
  const [ref, isInView] = useInView({ threshold: 0.2 });

  const stats = [
    { number: '500+', label: 'EVENTI REALIZZATI' },
    { number: '15+', label: 'ANNI DI ESPERIENZA' },
    { number: '200+', label: 'CLIENTI SODDISFATTI' },
  ];

  const images = [
    'https://public.readdy.ai/ai/img_res/08dfd5a3fc2c79cd33cf916d5fae92d5.jpg',
    'https://readdy.ai/api/search-image?query=elegant%20corporate%20event%20with%20sophisticated%20attendees%20networking%20in%20luxury%20venue%20professional%20business%20conference%20with%20modern%20setup%20and%20ambient%20lighting&width=600&height=400&seq=about2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=motorsport%20racing%20scene%20with%20high-performance%20car%20on%20professional%20track%20dynamic%20action%20shot%20with%20speed%20and%20precision%20professional%20sports%20photography&width=600&height=400&seq=about3&orientation=landscape',
    'https://readdy.ai/api/search-image?query=celebration%20moment%20at%20corporate%20event%20successful%20team%20celebrating%20achievement%20with%20joy%20and%20emotion%20in%20elegant%20modern%20venue%20warm%20lighting&width=600&height=400&seq=about4&orientation=landscape',
  ];

  return (
    <section id="chi-siamo" ref={ref} className="py-24 md:py-32 bg-white px-4">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-xs font-bold uppercase mb-6 whitespace-nowrap">
              Chi Siamo
            </div>
            
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-dark mb-4">
              Big Finance Srl
            </h2>
            
            <p className="text-gray-dark text-lg md:text-xl font-medium mb-6">
              La tua guida nel mondo degli eventi
            </p>
            
            <div className="space-y-4 text-gray-medium leading-relaxed">
              <p>
                Big Finance Srl è una società innovativa con sede legale a Roma e operativa su tutto il territorio italiano ed europeo. Con una solida esperienza nel settore dell'organizzazione di eventi, marketing e comunicazione, ci specializziamo nel creare esperienze memorabili e soluzioni strategiche per aziende e brand di ogni dimensione.
              </p>
              <p>
                La nostra missione è aiutare i nostri clienti a raggiungere il successo attraverso eventi impeccabili, campagne pubblicitarie mirate e ricerche di mercato precise e approfondite. Siamo esperti nell'organizzazione di eventi di ogni tipo, dalle fiere internazionali agli eventi di motorsport di alto livello.
              </p>
              <p>
                La nostra competenza nel settore ci permette di offrire soluzioni a 360° che uniscono creatività, strategia e professionalità. Siamo in grado di gestire progetti complessi con precisione, garantendo il massimo risultato in tempi brevi.
              </p>
            </div>
          </motion.div>

          {/* Right Images Grid */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="grid grid-cols-2 gap-4"
          >
            <div className="col-span-2 md:col-span-1 row-span-2 w-full h-full">
              <div className="relative group overflow-hidden rounded-2xl w-full h-full min-h-[400px]">
                <img
                  src={images[0]}
                  alt="Team Big Finance"
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 transition-all duration-300 flex items-center justify-center">
                  <i className="ri-add-line text-white text-4xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                </div>
              </div>
            </div>
            
            <div className="w-full h-full">
              <div className="relative group overflow-hidden rounded-2xl w-full h-full min-h-[190px]">
                <img
                  src={images[1]}
                  alt="Eventi Corporate"
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 transition-all duration-300 flex items-center justify-center">
                  <i className="ri-add-line text-white text-4xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                </div>
              </div>
            </div>
            
            <div className="w-full h-full">
              <div className="relative group overflow-hidden rounded-2xl w-full h-full min-h-[190px]">
                <img
                  src={images[2]}
                  alt="Motorsport"
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 transition-all duration-300 flex items-center justify-center">
                  <i className="ri-add-line text-white text-4xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20"
        >
          {stats.map((stat, index) => (
            <div key={index} className="text-center relative">
              <div className="text-5xl md:text-6xl font-black text-primary mb-2">
                {stat.number}
              </div>
              <div className="text-xs font-bold text-gray-dark uppercase tracking-wider">
                {stat.label}
              </div>
              {index < stats.length - 1 && (
                <div className="hidden md:block absolute right-0 top-1/2 transform -translate-y-1/2 w-px h-16 bg-gray-light"></div>
              )}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}